import Cookies from "universal-cookie";

const cookies = new Cookies();

/**
 * Sesión del cliente, del lado del navegador.
 *
 * El token vive en DOS cookies con el mismo nombre y distinto dueño:
 *
 *   1. la que setea la API al loguear (api.micuenta.blue, httpOnly, domain `.micuenta.blue`):
 *      es la que el navegador manda solo en cada llamada y la primera que la API lee.
 *   2. la que guarda AuthContext desde JS (alumnos.micuenta.blue, host-only, 7 días): el
 *      navegador NO la manda a api.micuenta.blue, así que para la API no existe.
 *
 * Hasta el 27-10-2026 la nº1 se seteaba SIN maxAge, o sea que era de sesión: se moría al cerrar
 * el navegador y el cliente quedaba "logueado" para el front y anónimo para la API. Medido ese
 * día en 14 minutos de tráfico real: 21 de 41 pedidos a las rutas de cuentas llegaban sin token.
 * Mientras la API no mire la sesión eso no se nota; el día que la mire, son 401 en la cara del
 * cliente. Dos cosas lo arreglan: el maxAge (ya está, del lado de la API) y mandar la nº2 en el
 * header `Authorization`, que es el fallback que la API ya soporta (`leerToken`). El header hace
 * falta igual porque hay navegadores que descartan la cookie de la API y no la de acá (webviews
 * de Instagram/WhatsApp, modos privados): 8 de esos 41 pedidos llegaron sin NINGUNA cookie.
 */
export const tokenDeSesion = () => cookies.get("token") || null;

// El código de acceso sale de `customer_context`, que lo guarda "Elegí el pedido" tomándolo del
// pedido mismo (orders.access_code). Es el único dato confiable que tenemos acá: el token lleva
// la fila del cliente, y ahí el código sería `username`, que en los tutores migrados de V3 es el
// NOMBRE de la persona (ver memoria login-dni-mcb-username). Prellenar eso en un campo oculto
// dejaría al cliente trabado sin poder corregirlo, así que si no hay contexto va al login común.
const codigoDeAcceso = () => {
  const contexto = cookies.get("customer_context");
  const codigo = contexto && (contexto.access_code || contexto.accessCode);
  return codigo ? String(codigo) : null;
};

// Pantallas que NO son "el cliente mirando su cuenta": el login (se rebotaría a sí mismo) y el
// formulario público de talles, que lo abre un tutor por link y no tiene sesión de MCB.
const pantallaSinSesion = () => {
  const ruta = (typeof window !== "undefined" && window.location.pathname) || "";
  return ruta === "/" || ruta.startsWith("/signin") || ruta.startsWith("/form-sizes");
};

// Un solo rebote por carga de página: el dashboard dispara varias llamadas en paralelo y las
// otras contestan 401 también.
let rebotando = false;

/**
 * La API dijo que no hay sesión. Antes esto llegaba a la pantalla como un error de consola y el
 * cliente se quedaba mirando una pantalla vacía o un "$ 0" que no era cierto. Ahora lo manda al
 * login con el código de acceso ya puesto, así solo tiene que escribir el DNI.
 */
export const sesionVencida = () => {
  if (rebotando || typeof window === "undefined" || pantallaSinSesion()) return;
  rebotando = true;

  const codigo = codigoDeAcceso();

  // La cookie de la API es httpOnly: desde acá no se puede borrar. Se limpia lo que sí es
  // nuestro, y el login sobrescribe el resto.
  cookies.remove("token", { path: "/" });
  cookies.remove("customer_context", { path: "/" });

  const destino = codigo
    ? `/signin/enriched/${encodeURIComponent(codigo)}/code?sesion=vencida`
    : "/?sesion=vencida";

  // replace y no push: la pantalla que se rompió no tiene que quedar en el historial.
  window.location.replace(destino);
};
